import React, { useMemo, useState } from 'react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { reclassifyBudget } from '../../../../services/budgetAPI';
import { buildFormStyles, money } from '../treasury/treasuryUi';

const BudgetReclassifyPanel = ({ ui, budget = null, canEdit = false, onChanged }) => {
  const styles = buildFormStyles(ui);
  const budgetedLines = useMemo(() => (budget?.lines || []).filter((line) => line.id), [budget]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!budget?.id) return;
    setBusy(true);
    setError('');
    try {
      const next = await reclassifyBudget(budget.id, {
        from_chart_account_id: Number(fromId),
        to_chart_account_id: Number(toId),
        month: Number(month),
        amount: Number(String(amount).replace(',', '.')),
        reason,
      });
      setAmount('');
      setReason('');
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo reclasificar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {canEdit ? (
        <form
          onSubmit={handleSubmit}
          style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12, display: 'grid', gap: 10 }}
        >
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Mover presupuesto entre cuentas</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <label style={styles.label}>
              Desde
              <select value={fromId} onChange={(event) => setFromId(event.target.value)} style={styles.input} required>
                <option value="">Cuenta origen</option>
                {budgetedLines.map((line) => (
                  <option key={line.chart_account_id} value={line.chart_account_id}>
                    {line.account_code} · {line.account_label}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Hacia
              <select value={toId} onChange={(event) => setToId(event.target.value)} style={styles.input} required>
                <option value="">Cuenta destino</option>
                {budgetedLines.map((line) => (
                  <option key={line.chart_account_id} value={line.chart_account_id}>
                    {line.account_code} · {line.account_label}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Mes
              <select value={month} onChange={(event) => setMonth(event.target.value)} style={styles.input}>
                {MONTH_LABELS.map((label, index) => (
                  <option key={label} value={String(index + 1)}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Monto
              <input value={amount} onChange={(event) => setAmount(event.target.value)} style={styles.input} required />
            </label>
          </div>
          <label style={styles.label}>
            Motivo
            <input value={reason} onChange={(event) => setReason(event.target.value)} style={styles.input} />
          </label>
          {error ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{error}</p> : null}
          <div>
            <button type="submit" style={styles.primaryBtn} disabled={busy}>
              {busy ? 'Moviendo…' : 'Reclasificar'}
            </button>
          </div>
        </form>
      ) : null}

      <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
        <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: ui.text }}>Historial</p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Fecha', 'Desde', 'Hacia', 'Mes', 'Monto', 'Motivo'].map((label) => (
                <th
                  key={label}
                  style={{
                    textAlign: label === 'Monto' ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(budget?.reclassifications || []).map((row) => (
              <tr key={row.id}>
                <td style={{ padding: 8, fontSize: 12 }}>{String(row.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td style={{ padding: 8, fontSize: 12 }}>
                  {row.from_account_code} {row.from_account_label}
                </td>
                <td style={{ padding: 8, fontSize: 12 }}>
                  {row.to_account_code} {row.to_account_label}
                </td>
                <td style={{ padding: 8, fontSize: 12 }}>{MONTH_LABELS[(row.month || 1) - 1]}</td>
                <td style={{ padding: 8, fontSize: 12, textAlign: 'right', fontWeight: 700 }}>{money(row.amount)}</td>
                <td style={{ padding: 8, fontSize: 12, color: ui.muted }}>{row.reason || '—'}</td>
              </tr>
            ))}
            {!(budget?.reclassifications || []).length ? (
              <tr>
                <td colSpan={6} style={{ padding: 16, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Sin reclasificaciones.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BudgetReclassifyPanel;
